import { FullProg_v4 } from '~/providers/types';
import { DamWeekDayKey } from '~/providers/types/api-private';
import { groupWeekSchedules } from './schedules';

/** Geometria do gráfico ilustrativo de ventilação do design (viewBox 700 × 352, eixo de 0 a 24 h). */
export const VENT_CHART_X0 = 64;
export const VENT_CHART_X1 = 668;
const DAY_MINUTES = 1440;
/** Largura mínima legível das faixas de ventilação, que em minutos ficariam finas na escala de 24 h. */
const MIN_BAND_PX = 16;

/** Janela usada no design quando o DAM não tem programação que permita refrigeração. */
export const DEFAULT_VENTILATION_WINDOW = { start: '08:00', end: '18:00' };

export interface VentilationWindow {
  start: string;
  end: string;
}

export interface VentilationChartGeometry {
  vent1X: number;
  vent1W: number;
  vent2X: number;
  vent2W: number;
  progX1: number;
  progX2: number;
}

export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

export function addMinutes(time: string, delta: number): string {
  const total = (((toMinutes(time) + delta) % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Programação que ilustra o gráfico: a de hoje que permite refrigeração, senão a primeira que permite. */
export function pickVentilationWindow(week: FullProg_v4['week'], today: DamWeekDayKey): VentilationWindow {
  const allowGroups = groupWeekSchedules(week).filter((group) => group.permission === 'allow');
  const group = allowGroups.find((item) => item.days.includes(today)) ?? allowGroups[0];
  return group ? { start: group.start, end: group.end } : DEFAULT_VENTILATION_WINDOW;
}

function xForMinute(minute: number): number {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, minute));
  return VENT_CHART_X0 + (clamped / DAY_MINUTES) * (VENT_CHART_X1 - VENT_CHART_X0);
}

/** Mesma conta do design: faixas no início e no fim da programação, com a duração da ventilação. */
export function computeVentilationChart(window: VentilationWindow, ventStart: number, ventEnd: number): VentilationChartGeometry {
  const startMin = toMinutes(window.start);
  const endRaw = toMinutes(window.end);
  const endMin = Math.min(endRaw > startMin ? endRaw : endRaw + DAY_MINUTES, DAY_MINUTES);
  const vent1X = xForMinute(startMin);
  const vent1W = Math.max(xForMinute(startMin + ventStart) - vent1X, MIN_BAND_PX);
  const progX2 = xForMinute(endMin);
  const vent2W = Math.max(progX2 - xForMinute(endMin - ventEnd), MIN_BAND_PX);
  return {
    vent1X, vent1W, vent2X: progX2 - vent2W, vent2W, progX1: vent1X, progX2,
  };
}

/**
 * "Modo ECO ao Longo de um Dia": o desenho do design (programação 08:00–18:00, setpoint 24 °C, histereses 1 °C,
 * LTI 18 °C) é reescalado para a configuração real. Na horizontal, o que fica dentro da programação é convertido
 * das coordenadas do design (x 310 a 910) para a janela; na vertical, 20 px por °C com o setpoint em y 230.
 * A conversão é feita nos pontos, e não com `transform` no SVG, para a espessura das linhas seguir a do design.
 */
export interface EcoDayChartInput {
  window: VentilationWindow;
  ventStart: number;
  ventEnd: number;
  setpoint: number;
  upperHysteresis: number;
  lowerHysteresis: number;
  lti: number;
}

export interface EcoDayChartGeometry {
  progX1: number;
  progX2: number;
  vent1W: number;
  vent2X: number;
  vent2W: number;
  upperY: number;
  lowerY: number;
  ltiY: number;
  labelY: { upper: number; lower: number; lti: number };
  temperaturePath: string;
  capacityPath: string;
  hourTicks: { x: number; label: string }[];
  temperatureTicks: { y: number; label: string }[];
}

const ECO_DAY_AXIS_X0 = 190;
const ECO_DAY_AXIS_WIDTH = 840;
const ECO_DAY_DESIGN_X1 = 310;
const ECO_DAY_DESIGN_X2 = 910;
const ECO_DAY_DESIGN_WIDTH = ECO_DAY_DESIGN_X2 - ECO_DAY_DESIGN_X1;
/** Onde o design volta a pedir capacidade parcial perto do fim da programação. */
const ECO_DAY_LAST_PARTIAL_X = 870;
/** Largura mínima legível das faixas de ventilação, como no design. */
const ECO_DAY_MIN_BAND_PX = 8;
const ECO_DAY_SETPOINT_Y = 230;
const ECO_DAY_PX_PER_DEGREE = 20;
const ECO_DAY_TOP_Y = 140;
const ECO_DAY_BOTTOM_Y = 390;
const ECO_DAY_LABEL_GAP = 16;
/** Curva de temperatura ilustrativa do design, nas coordenadas dele. */
const ECO_DAY_TEMPERATURE_POINTS: [number, number][] = [
  [310, 267.7], [328, 252], [350, 232], [384, 224], [428, 218], [460, 214], [482, 210], [488, 202], [490, 206], [497, 252],
  [505, 258], [517, 262], [550, 266], [574, 270], [619, 294], [664, 326], [678, 340], [690, 350], [705, 362], [720, 350],
  [740, 326], [776, 286], [809, 250], [821, 240], [846, 222], [870, 207], [890, 211], [910, 214],
];
/** Referências do design: histerese superior, setpoint, histerese inferior e LTI. */
const ECO_DAY_DESIGN_LEVELS = {
  upper: 210, setpoint: 230, lower: 250, lti: 350,
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function roundCoord(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Leva um y da curva do design para os níveis reais, trecho a trecho entre as referências. */
function mapTemperatureY(y: number, levels: { upper: number; lower: number; lti: number }): number {
  const design = ECO_DAY_DESIGN_LEVELS;
  const anchors: [number, number][] = [[design.upper, levels.upper], [design.setpoint, ECO_DAY_SETPOINT_Y], [design.lower, levels.lower], [design.lti, levels.lti]];
  if (y <= design.upper) return y + (levels.upper - design.upper);
  if (y >= design.lti) return y + (levels.lti - design.lti);
  for (let index = 0; index < anchors.length - 1; index += 1) {
    const [fromDesign, fromReal] = anchors[index];
    const [toDesign, toReal] = anchors[index + 1];
    if (y <= toDesign) return fromReal + ((y - fromDesign) / (toDesign - fromDesign)) * (toReal - fromReal);
  }
  return y;
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

export function computeEcoDayChart(input: EcoDayChartInput): EcoDayChartGeometry {
  const startMin = toMinutes(input.window.start);
  const endRaw = toMinutes(input.window.end);
  const endMin = endRaw > startMin ? endRaw : DAY_MINUTES;

  // Eixo de horas: duas horas de folga em cada lado da programação, em horas pares (no design, 06:00 a 20:00).
  const axisStartHour = Math.max(0, Math.floor((startMin / 60 - 2) / 2) * 2);
  const axisEndHour = Math.min(24, Math.ceil((endMin / 60 + 2) / 2) * 2);
  const pxPerMinute = ECO_DAY_AXIS_WIDTH / ((axisEndHour - axisStartHour) * 60);
  const xForMinute = (minute: number): number => ECO_DAY_AXIS_X0 + (minute - axisStartHour * 60) * pxPerMinute;
  const tickStep = axisEndHour - axisStartHour <= 16 ? 2 : 4;
  const hourTicks: { x: number; label: string }[] = [];
  for (let hour = axisStartHour; hour <= axisEndHour; hour += tickStep) {
    hourTicks.push({ x: roundCoord(xForMinute(hour * 60)), label: formatHour(hour) });
  }

  const progX1 = xForMinute(startMin);
  const progX2 = xForMinute(endMin);
  const scale = (progX2 - progX1) / ECO_DAY_DESIGN_WIDTH;
  const toChartX = (designX: number): number => roundCoord(progX1 + (designX - ECO_DAY_DESIGN_X1) * scale);
  const bandWidth = (minutes: number): number => (minutes > 0 ? Math.min(Math.max(minutes * pxPerMinute, ECO_DAY_MIN_BAND_PX), (progX2 - progX1) / 2) : 0);
  const vent1W = bandWidth(input.ventStart);
  const vent2W = bandWidth(input.ventEnd);

  // Último degrau de capacidade antes da ventilação final, na conta do design (em coordenadas do design).
  const vent2DesignX = ECO_DAY_DESIGN_X2 - (scale > 0 ? vent2W / scale : 0);
  const tail = vent2DesignX > ECO_DAY_LAST_PARTIAL_X + 2
    ? `V500 H${toChartX(ECO_DAY_LAST_PARTIAL_X)} V465 H${toChartX(vent2DesignX)} V500 H${toChartX(ECO_DAY_DESIGN_X2)}`
    : `V500 H${toChartX(ECO_DAY_DESIGN_X2)}`;
  // Degraus da curva de capacidade do design (x do design, y fixo).
  const capacitySteps = ['V465', 490, 'V430', 505, 'V465', 517, 'V500', 690, 'V535', 809];
  let capacityPath = `M${toChartX(ECO_DAY_DESIGN_X1)},500 H${toChartX(482)}`;
  for (let index = 0; index < capacitySteps.length; index += 2) {
    capacityPath += ` ${capacitySteps[index]} H${toChartX(Number(capacitySteps[index + 1]))}`;
  }

  const upperY = clamp(ECO_DAY_SETPOINT_Y - input.upperHysteresis * ECO_DAY_PX_PER_DEGREE, ECO_DAY_TOP_Y, ECO_DAY_SETPOINT_Y - 2);
  const lowerY = clamp(ECO_DAY_SETPOINT_Y + input.lowerHysteresis * ECO_DAY_PX_PER_DEGREE, ECO_DAY_SETPOINT_Y + 2, ECO_DAY_BOTTOM_Y - 30);
  const ltiY = clamp(ECO_DAY_SETPOINT_Y + (input.setpoint - input.lti) * ECO_DAY_PX_PER_DEGREE, lowerY + 10, ECO_DAY_BOTTOM_Y);
  const levels = { upper: upperY, lower: lowerY, lti: ltiY };
  const temperaturePath = ECO_DAY_TEMPERATURE_POINTS
    .map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${toChartX(x)},${roundCoord(mapTemperatureY(y, levels))}`)
    .join(' ');

  // Rótulos de temperatura a cada 2 °C nas linhas de grade do design, relativos ao setpoint.
  const temperatureTicks = [154, 194, 234, 274, 314, 354].map((y) => ({
    y,
    label: `${roundCoord(input.setpoint + (ECO_DAY_SETPOINT_Y + 4 - y) / ECO_DAY_PX_PER_DEGREE)} °C`,
  }));
  const setpointLabelY = ECO_DAY_SETPOINT_Y + 4;
  const lowerLabelY = Math.max(lowerY + 4, setpointLabelY + ECO_DAY_LABEL_GAP);

  return {
    progX1: roundCoord(progX1),
    progX2: roundCoord(progX2),
    vent1W: roundCoord(vent1W),
    vent2X: roundCoord(progX2 - vent2W),
    vent2W: roundCoord(vent2W),
    upperY: roundCoord(upperY),
    lowerY: roundCoord(lowerY),
    ltiY: roundCoord(ltiY),
    labelY: {
      upper: Math.min(upperY + 4, setpointLabelY - ECO_DAY_LABEL_GAP),
      lower: lowerLabelY,
      lti: Math.max(ltiY + 4, lowerLabelY + ECO_DAY_LABEL_GAP),
    },
    temperaturePath,
    capacityPath: `${capacityPath} ${tail}`,
    hourTicks,
    temperatureTicks,
  };
}
